// Copyright ©️ 2026 eVolve MEP, LLC

import { type Task, type RenderableUser, useEvolvePatch } from '@evolvemep/api-ts';
import { InputLabel, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';

import { MultiUserSelect } from 'components/Mantine/MultiUserSelect';
import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';

type Props = {
  title?: string;
  opened: boolean;
  onClose: (assigneesUpdated: boolean) => void;
  task: Pick<Task, 'taskId' | 'assignees'>;
  onTaskUpdated: (updatedTask: Task) => void;
  startDirty?: boolean;
};

type FormValues = {
  users: RenderableUser[];
};

const AssigneeFields = ({
  title,
  task,
  onTaskUpdated,
  onClose,
}: Pick<Props, 'title' | 'task' | 'onTaskUpdated' | 'onClose'>) => {
  const { apiCall: patchAssignees } = useEvolvePatch(`shop/task/${task.taskId}`);
  const form = useForm<FormValues>({
    initialValues: {
      users: task.assignees.map(({ user }) => user),
    },
  });

  const onSubmit = async (values: typeof form.values) => {
    await patchAssignees({ assignees: values.users.map((a) => a.userId) })
      .then(onTaskUpdated)
      .then(() => onClose(true));
  };

  useFormPopover(form, onSubmit);

  return (
    <Stack mah={400} gap={0}>
      <InputLabel>{title}</InputLabel>
      <MultiUserSelect value={form.values.users} onChange={(v) => form.setFieldValue('users', v ?? [])} />
    </Stack>
  );
};

export const EditTaskAssigneesPopover = ({
  opened,
  onClose,
  task,
  title = task.assignees.length === 0 ? 'Add assignees' : 'Edit assignees',
  onTaskUpdated,
  startDirty,
}: Props) => (
  <FormPopover
    opened={opened}
    onClose={() => onClose(false)}
    // The success path must close with `onClose(true)`, not the default `onClose(false)` above
    closeOnConfirm={false}
    startDirty={startDirty}
    confirmationText={task.assignees.length === 0 ? 'Add' : 'Save'}
    width="auto"
  >
    <AssigneeFields title={title} task={task} onTaskUpdated={onTaskUpdated} onClose={onClose} />
  </FormPopover>
);
